// src/components/VerificationBadge.jsx
// Displays the 100-point verification score breakdown.
// Used in FeedCard expanded view and admin panel.
//
// Shows:
//   Score (0-100) with tier label
//   Factor breakdown: reporter / photo / GPS / multiple reports / official source / recency
//   Conflict warning when isConflicting = true
//   Pipeline state pill: unverified → pending review → multi-source verified → confirmed

import { useState } from 'react';
import { T }        from '@components/theme';

const TIER_STYLES = {
  verified:          { color: '#059669', bg: 'rgba(5,150,105,0.10)',  label: 'Verified'          },
  high_confidence:   { color: '#0369a1', bg: 'rgba(3,105,161,0.10)',  label: 'High Confidence'   },
  medium_confidence: { color: '#d97706', bg: 'rgba(217,119,6,0.10)',  label: 'Medium Confidence' },
  unverified:        { color: '#6b7280', bg: 'rgba(107,114,128,0.08)',label: 'Unverified'        },
};

const PIPELINE_STATES = {
  unverified:           { label: 'Unverified',             color: '#6b7280', step: 0 },
  pending_review:       { label: 'Pending Review',         color: '#d97706', step: 1 },
  multi_source_verified:{ label: 'Multi-Source Verified',  color: '#0369a1', step: 2 },
  confirmed:            { label: 'Confirmed',              color: '#059669', step: 3 },
  active:               { label: 'Active',                 color: '#059669', step: 3 },
  resolved:             { label: 'Resolved',               color: '#6b7280', step: 4 },
};

const FACTOR_ICONS = {
  reporter_reputation: '👤',
  photo_attached:      '📸',
  gps_attached:        '📍',
  multiple_reports:    '👥',
  official_source:     '📡',
  recent_report:       '🕐',
};

export function VerificationBadge({ report, compact = false }) {
  const [open, setOpen] = useState(false);

  const score    = report.verificationScore;
  const tier     = TIERS[report.verificationTier] ?? TIERS.unverified;
  const factors  = report.verificationFactors ?? [];
  const pState   = PIPELINE_STATES[report.status] ?? PIPELINE_STATES.unverified;
  const conflict = report.isConflicting;

  // Compact mode — just a pill
  if (compact || score == null) {
    return (
      <span style={{
        display:     'inline-flex', alignItems: 'center', gap: 4,
        padding:     '2px 7px', borderRadius: 5, fontSize: 9, fontWeight: 700,
        background:   conflict ? 'rgba(220,38,38,0.10)' : (TIERS[report.verificationTier]?.bg ?? 'rgba(107,114,128,0.08)'),
        color:        conflict ? '#dc2626' : (TIERS[report.verificationTier]?.color ?? '#6b7280'),
        border:      `1px solid ${conflict ? 'rgba(220,38,38,0.25)' : 'rgba(255,255,255,0.08)'}`,
        cursor:       factors.length > 0 ? 'pointer' : 'default',
      }} onClick={() => factors.length > 0 && setOpen(v => !v)}>
        {conflict ? '⚠ Conflicting' : score != null ? `${score}/100` : 'Unscored'}
      </span>
    );
  }

  return (
    <div>
      {/* Main badge button */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display:      'flex', alignItems: 'center', gap: 6,
          padding:      '4px 8px', borderRadius: 7,
          background:    conflict ? 'rgba(220,38,38,0.10)' : (TIERS[report.verificationTier]?.bg ?? 'rgba(107,114,128,0.08)'),
          border:       `1px solid ${conflict ? 'rgba(220,38,38,0.25)' : 'rgba(255,255,255,0.08)'}`,
          cursor:       'pointer', fontFamily: T.font, textAlign: 'left',
        }}
      >
        {conflict ? (
          <>
            <span style={{ fontSize: 12 }}>⚠️</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#dc2626' }}>
              Conflicting Reports — Manual Review Needed
            </span>
          </>
        ) : (
          <>
            <div style={{
              fontWeight: 900, fontSize: 14, lineHeight: 1,
              color: TIERS[report.verificationTier]?.color ?? '#6b7280',
            }}>
              {score ?? '—'}<span style={{ fontSize: 9, opacity: 0.7 }}>/100</span>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: TIERS[report.verificationTier]?.color ?? '#6b7280' }}>
                {TIERS[report.verificationTier]?.label ?? 'Unscored'}
              </div>
              <div style={{ fontSize: 9, color: pState.color }}>{pState.label}</div>
            </div>
            <span style={{ fontSize: 10, color: T.dim, marginLeft: 'auto' }}>{open ? '▲' : '▼'}</span>
          </>
        )}
      </button>

      {/* Expanded factor breakdown */}
      {open && factors.length > 0 && (
        <div style={{
          marginTop: 6, padding: '8px 10px',
          background: '#111', border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 8,
        }}>
          {/* Pipeline progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10 }}>
            {['unverified','pending_review','multi_source_verified','confirmed'].map((s, i) => {
              const ps = PIPELINE_STATES[s];
              const isCurrent = report.status === s;
              const isPast    = (PIPELINE_STATES[report.status]?.step ?? 0) > i;
              return (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: isCurrent || isPast ? ps.color : 'rgba(255,255,255,0.1)',
                    border: isCurrent ? `2px solid ${ps.color}` : 'none',
                  }} />
                  {i < 3 && <div style={{ width: 16, height: 1, background: isPast ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)' }} />}
                </div>
              );
            })}
            <span style={{ fontSize: 9, color: pState.color, fontWeight: 700, marginLeft: 4 }}>
              {pState.label}
            </span>
          </div>

          {/* Factor rows */}
          {factors.map(f => (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '4px 0',
              borderBottom: '1px solid rgba(255,255,255,0.04)',
            }}>
              <span style={{ fontSize: 13, flexShrink: 0 }}>{FACTOR_ICONS[f.id] ?? '•'}</span>
              <span style={{ fontSize: 10, color: T.text, flex: 1, lineHeight: 1.3 }}>{f.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <div style={{
                  width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.07)', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.round((f.earned / f.max) * 100)}%`,
                    height: '100%', borderRadius: 2,
                    background: f.earned === f.max ? '#059669' : f.earned > 0 ? '#d97706' : 'transparent',
                  }} />
                </div>
                <span style={{ fontSize: 9, fontWeight: 800, color: f.earned > 0 ? '#d1d5db' : '#4b5563', width: 28, textAlign: 'right' }}>
                  {f.earned}/{f.max}
                </span>
              </div>
            </div>
          ))}

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, marginTop: 4 }}>
            <span style={{ fontSize: 10, color: T.dim }}>Total score</span>
            <span style={{ fontSize: 12, fontWeight: 900, color: TIERS[report.verificationTier]?.color ?? '#6b7280' }}>
              {score}/100 — {TIERS[report.verificationTier]?.label ?? 'Unscored'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper for compact display
const TIERS = TIER_STYLES;
